import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CourseCard } from "@/components/CourseCard";
import { detectGeo } from "@/lib/geo";

const ALL = "__all__";

export default function Search() {
  const { categorySlug } = useParams();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [geoCountry, setGeoCountry] = useState("");

  const [state, setState] = useState({
    category: categorySlug || params.get("category") || "",
    country_id: params.get("country_id") || "",
    city_id: params.get("city_id") || "",
    institution_id: params.get("institution_id") || "",
    level: params.get("level") || "",
    specialisation: params.get("specialisation") || "",
    mode: params.get("mode") || "",
    min_fee: params.get("min_fee") || "",
    max_fee: params.get("max_fee") || "",
    sort: params.get("sort") || "recommended",
    page: 1,
  });

  useEffect(() => {
    api.get("/public/filters").then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    detectGeo().then((g) => setGeoCountry(g.country && g.country !== "UNKNOWN" ? g.country : "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (categorySlug) setState((s) => ({ ...s, category: categorySlug }));
  }, [categorySlug]);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    const p = { q, ...state };
    if (geoCountry) p.geo_country = geoCountry;
    Object.keys(p).forEach((k) => (p[k] === "" || p[k] == null) && delete p[k]);
    try {
      const { data } = await api.get("/public/search", { params: p });
      setResult(data);
    } catch {
      setResult({ results: [], total: 0, university_count: 0 });
    } finally {
      setLoading(false);
    }
  }, [q, state, geoCountry]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v === ALL ? "" : v, page: 1 }));

  const activeCategory = filters?.categories?.find((c) => c.slug === state.category || c.id === state.category);
  const cities = filters?.cities?.filter((c) => !state.country_id || c.country_id === state.country_id) || [];

  const submitSearch = (e) => {
    e.preventDefault();
    setState((s) => ({ ...s, page: 1 }));
    params.set("q", q);
    setParams(params);
  };

  const clearAll = () => {
    setState({ category: "", country_id: "", city_id: "", institution_id: "", level: "", specialisation: "",
      mode: "", min_fee: "", max_fee: "", sort: "recommended", page: 1 });
    setQ("");
  };

  const FilterPanel = (
    <div className="space-y-5" data-testid="filters-panel">
      <FilterSelect label="Course Category" value={state.category} onChange={(v) => set("category", v)}
        options={(filters?.categories || []).map((c) => ({ value: c.slug, label: c.name }))} />
      <FilterSelect label="Country" value={state.country_id} onChange={(v) => { set("country_id", v); set("city_id", ""); }}
        options={(filters?.countries || []).map((c) => ({ value: c.id, label: c.name }))} />
      <FilterSelect label="City" value={state.city_id} onChange={(v) => set("city_id", v)}
        options={cities.map((c) => ({ value: c.id, label: c.name }))} />
      <FilterSelect label="University" value={state.institution_id} onChange={(v) => set("institution_id", v)}
        options={(filters?.universities || []).map((u) => ({ value: u.id, label: u.name }))} />
      <FilterSelect label="Course Level" value={state.level} onChange={(v) => set("level", v)}
        options={(filters?.levels || []).map((l) => ({ value: l, label: l }))} />
      <FilterSelect label="Specialisation" value={state.specialisation} onChange={(v) => set("specialisation", v)}
        options={(filters?.specialisations || []).map((s) => ({ value: s.slug, label: s.name }))} />
      <FilterSelect label="Mode of Study" value={state.mode} onChange={(v) => set("mode", v)}
        options={(filters?.modes || []).map((m) => ({ value: m, label: m }))} />
      <div>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Fee Range</label>
        <div className="flex gap-2">
          <Input type="number" placeholder="Min" value={state.min_fee}
            onChange={(e) => set("min_fee", e.target.value)} data-testid="filter-min-fee" />
          <Input type="number" placeholder="Max" value={state.max_fee}
            onChange={(e) => set("max_fee", e.target.value)} data-testid="filter-max-fee" />
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={clearAll} className="text-slate-500" data-testid="clear-filters-btn">
        <X className="mr-1 h-4 w-4" /> Clear all filters
      </Button>
    </div>
  );

  return (
    <div className="bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <form onSubmit={submitSearch} className="flex gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3">
            <SearchIcon className="h-5 w-5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search courses, universities, specialisations..."
              className="border-0 shadow-none focus-visible:ring-0" data-testid="search-input" />
          </div>
          <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="search-btn">Search</Button>
        </form>

        <div className="mt-6">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            {activeCategory ? activeCategory.name : q ? `Results for "${q}"` : "All Online Courses"}
          </h1>
          <p className="mt-1 text-slate-500" data-testid="results-count">
            {result ? `${result.university_count} Universities | ${result.total} Programmes` : "Loading..."}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-24 rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 font-heading font-semibold text-slate-900">Filters</h3>
              {FilterPanel}
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="mb-4 flex items-center justify-between">
              <Sheet>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="outline" size="sm" data-testid="mobile-filters-btn">
                    <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 overflow-y-auto">
                  <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                  <div className="mt-6">{FilterPanel}</div>
                </SheetContent>
              </Sheet>
              <div className="ml-auto flex items-center gap-2">
                <span className="hidden text-sm text-slate-500 sm:inline">Sort by</span>
                <Select value={state.sort} onValueChange={(v) => set("sort", v)}>
                  <SelectTrigger className="w-[180px] bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recommended">Recommended</SelectItem>
                    <SelectItem value="name">University Name</SelectItem>
                    <SelectItem value="course_name">Course Name</SelectItem>
                    <SelectItem value="fee_asc">Fee: Low to High</SelectItem>
                    <SelectItem value="fee_desc">Fee: High to Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => <div key={i} className="h-40 animate-pulse rounded-xl bg-slate-200" />)}
              </div>
            ) : result?.results?.length ? (
              <div className="space-y-4">
                {result.results.map((c) => <CourseCard key={c.id} course={c} />)}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
                <p className="font-heading text-lg font-semibold text-slate-900">No programmes found</p>
                <p className="mt-1 text-slate-500">Try adjusting your search or filters.</p>
              </div>
            )}

            {result && result.total > result.per_page && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button variant="outline" disabled={state.page <= 1}
                  onClick={() => setState((s) => ({ ...s, page: s.page - 1 }))} data-testid="prev-page-btn">Previous</Button>
                <span className="px-3 text-sm text-slate-600">Page {result.page} of {Math.ceil(result.total / result.per_page)}</span>
                <Button variant="outline" disabled={state.page >= Math.ceil(result.total / result.per_page)}
                  onClick={() => setState((s) => ({ ...s, page: s.page + 1 }))} data-testid="next-page-btn">Next</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <Select value={value || ALL} onValueChange={onChange}>
        <SelectTrigger className="bg-white"><SelectValue placeholder={`All`} /></SelectTrigger>
        <SelectContent className="max-h-64">
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
